import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw, ArrowRight, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/authService';

const CashFlowTransfers = () => {
  const [transfers, setTransfers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [formData, setFormData] = useState({
    from_account_id: '',
    to_account_id: '',
    amount: '',
    transfer_date: new Date().toISOString().split('T')[0],
    description: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [transfersRes, banksRes] = await Promise.all([
        api.get('/api/cash-flow/transfers'),
        api.get('/api/cash-flow/banks')
      ]);

      setTransfers(transfersRes.data.data || []);
      setBankAccounts(banksRes.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar transferências');
      // Garantir que os arrays sejam definidos mesmo em caso de erro
      setTransfers([]);
      setBankAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.from_account_id === formData.to_account_id) {
      toast.error('Conta de origem e destino devem ser diferentes!');
      return;
    }

    try {
      await api.post('/api/cash-flow/transfers', formData);
      toast.success('Transferência realizada com sucesso!');
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Erro ao realizar transferência:', error);
      toast.error(error.response?.data?.message || 'Erro ao realizar transferência');
    }
  };

  const handleCancel = async (id) => {
    if (!confirm('Cancelar esta transferência?')) return;

    try {
      await api.delete(`/api/cash-flow/transfers/${id}`);
      toast.success('Transferência cancelada!');
      loadData();
    } catch (error) {
      toast.error('Erro ao cancelar transferência');
    }
  };

  const resetForm = () => {
    setFormData({
      from_account_id: '',
      to_account_id: '',
      amount: '',
      transfer_date: new Date().toISOString().split('T')[0],
      description: '',
      notes: ''
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const getAccountBalance = (accountId) => {
    const account = bankAccounts.find((acc) => acc.id === parseInt(accountId));
    return account ? account.current_balance : 0;
  };

  const getStatusBadge = (status) => {
    if (status === 'completed') {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
          Concluída
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
        Cancelada
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Transferências</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Movimentar valores entre contas
          </p>
        </div>

        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Nova Transferência
        </button>
      </div>

      {/* Resumo das Contas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {bankAccounts.map((account) => (
          <div
            key={account.id}
            className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow"
          >
            <p className="text-sm text-gray-600 dark:text-gray-400">{account.name}</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(account.current_balance)}
            </p>
          </div>
        ))}
      </div>

      {/* Lista de Transferências */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  De
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">

                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Para
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Valor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {transfers.map((transfer) => (
                <tr key={transfer.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {new Date(transfer.transfer_date).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    <div>
                      <p className="font-medium">{transfer.from_account_name}</p>
                      {transfer.from_bank_name && (
                        <p className="text-xs text-gray-500">{transfer.from_bank_name}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <ArrowRight className="w-5 h-5 text-blue-500 mx-auto" />
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    <div>
                      <p className="font-medium">{transfer.to_account_name}</p>
                      {transfer.to_bank_name && (
                        <p className="text-xs text-gray-500">{transfer.to_bank_name}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                    {formatCurrency(transfer.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(transfer.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {transfer.status === 'completed' && (
                      <button
                        onClick={() => handleCancel(transfer.id)}
                        className="text-red-600 hover:text-red-700"
                        title="Cancelar"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {transfers.length === 0 && !loading && (
            <div className="text-center py-12">
              <RefreshCw className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Nenhuma transferência realizada</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Nova Transferência */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                Nova Transferência
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Conta de Origem *
                    </label>
                    <select
                      value={formData.from_account_id}
                      onChange={(e) => setFormData({ ...formData, from_account_id: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                      required
                    >
                      <option value="">Selecione...</option>
                      {bankAccounts.map((acc) => (
                        <option key={acc.id} value={acc.id}>
                          {acc.name} - {formatCurrency(acc.current_balance)}
                        </option>
                      ))}
                    </select>
                    {formData.from_account_id && (
                      <p className="text-xs text-gray-500 mt-1">
                        Saldo: {formatCurrency(getAccountBalance(formData.from_account_id))}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Conta de Destino *
                    </label>
                    <select
                      value={formData.to_account_id}
                      onChange={(e) => setFormData({ ...formData, to_account_id: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                      required
                    >
                      <option value="">Selecione...</option>
                      {bankAccounts
                        .filter((acc) => acc.id !== parseInt(formData.from_account_id))
                        .map((acc) => (
                          <option key={acc.id} value={acc.id}>
                            {acc.name} - {formatCurrency(acc.current_balance)}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Valor *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Data *
                    </label>
                    <input
                      type="date"
                      value={formData.transfer_date}
                      onChange={(e) => setFormData({ ...formData, transfer_date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Descrição
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    placeholder="Ex: Depósito bancário"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Observações
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                  />
                </div>

                {formData.from_account_id && formData.amount && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Saldo após transferência:
                    </p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {formatCurrency(getAccountBalance(formData.from_account_id) - parseFloat(formData.amount || 0))}
                    </p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    Transferir
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashFlowTransfers;